import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import cloneDeep from 'lodash/cloneDeep';
import { useCookies } from 'next-client-cookies';
import usePostMessage from 'helpers/hooks/useFujifilmNavigation';
import useI18n from 'helpers/hooks/useI18n';
import usePath from 'helpers/hooks/usePath';
import { useRouter } from 'i18n/routing';
import { ShippingMethod } from 'types/entity/cart';
import { Category } from 'types/entity/category';
import { Variant } from 'types/entity/product';
import { Wishlist, LineItem as WishlistLineItem } from 'types/entity/wishlist';
import { BooleanFacet, FacetConfiguration, Term, TermFacet } from '../types';
import { refinementRemovedEventName, refinementsClearedEventName } from './constants';
import { ActiveRefinement, ProductListContextShape, RefinementRemovedEvent, Sort, UiState } from './types';

export const ProductListContext = createContext<ProductListContextShape>({
  categories: [],
  facetsConfiguration: {},
  totalItems: 0,
  activeRefinements: [],
  refine() {},
  replaceSort() {},
  removeAllRefinements() {},
  loadMore() {},
  isContainsP2RItems: false,
});

export interface ProductListPropsProps {
  uiState: UiState;
  categories: Category[];
  shippingMethods?: ShippingMethod[];
  facetsConfiguration: Record<string, FacetConfiguration>;
  wishlist?: Wishlist;
  addToWishlist?: (lineItem: WishlistLineItem, count: number) => Promise<void>;
  removeFromWishlist?: (item: WishlistLineItem) => Promise<void>;
  onAddToCart?: (variant: Variant, quantity: number) => Promise<void>;
  isContainsP2RItems?: boolean;
}

const getMatchingCategoryIdsByTags = (categories: Category[], selectedTags: string[]) => {
  const matchingCategoryIds = new Set<string>();
  const selectedTagsLower = selectedTags.map((tag) => tag.toLowerCase());

  const traverseCategories = (cats: any[]) => {
    cats.forEach((cat) => {
      if (cat.categoryTags) {
        const catTags = Array.isArray(cat.categoryTags)
          ? cat.categoryTags.map((t: string) => t.toLowerCase())
          : Object.keys(cat.categoryTags).map((t) => t.toLowerCase());

        if (selectedTagsLower.some((tag) => catTags.includes(tag))) {
          if (cat.categoryId) matchingCategoryIds.add(cat.categoryId.toString());
        }
      }

      if (cat.descendants && cat.descendants.length > 0) {
        traverseCategories(cat.descendants);
      }
    });
  };

  traverseCategories(categories);
  return matchingCategoryIds;
};

const createTermRefinementReset = (configuration: FacetConfiguration, term: Term) => () => {
  term.selected = false;
  configuration.selected = (configuration as TermFacet).terms.some((entry: Term) => entry.selected);
};

const ProductListProvider = ({
  children,
  categories,
  shippingMethods,
  uiState,
  facetsConfiguration: initialFacetsConfiguration,
  wishlist,
  addToWishlist,
  removeFromWishlist,
  onAddToCart,
  isContainsP2RItems,
}: React.PropsWithChildren<ProductListPropsProps>) => {
  const router = useRouter();

  const { pathWithoutQuery } = usePath();

  const cookies = useCookies();
  const locale = cookies.get('locale') as string;

  const searchParams = useSearchParams();

  const mergedFacetsConfiguration = useMemo(() => {
    const config = cloneDeep(initialFacetsConfiguration);

    Array.from(searchParams.entries()).forEach(([key, value]) => {
      // term/color facets: facets[attribute][terms][index]
      const termMatch = key.match(/^facets\[(.+)\]\[terms\]\[\d+\]$/);
      if (termMatch) {
        const attribute = termMatch[1];
        if (!config[attribute]) {
          config[attribute] = {
            key: attribute,
            identifier: attribute,
            label: attribute,
            type: 'term',
            selected: false,
            terms: [],
          } as TermFacet;
        }
        const facet = config[attribute] as TermFacet;
        let term = facet.terms.find((t) => t.key === value);
        if (!term) {
          term = {
            key: value,
            identifier: value,
            label: value,
            selected: false,
            count: 0,
          };
          facet.terms.push(term);
        }
        term.selected = true;
        facet.selected = true;
      }

      // boolean facets: facets[attribute][boolean]
      const booleanMatch = key.match(/^facets\[(.+)\]\[boolean\]$/);
      if (booleanMatch) {
        const attribute = booleanMatch[1];
        if (!config[attribute]) {
          config[attribute] = {
            key: attribute,
            identifier: attribute,
            label: attribute,
            type: 'boolean',
            selected: false,
            terms: [],
          } as BooleanFacet;
        }
        const facet = config[attribute] as BooleanFacet;
        let term = facet.terms.find((t) => t.key === value);
        if (!term) {
          term = {
            key: value,
            identifier: value,
            label: value,
            selected: false,
            count: 0,
          };
          facet.terms.push(term);
        }
        term.selected = true;
        facet.selected = true;
      }
    });

    return config;
  }, [initialFacetsConfiguration, searchParams]);

  const limit = searchParams.get('limit');
  const { postMessageNav } = usePostMessage();
  const { currency } = useI18n();

  const activeSort = useMemo<Sort | undefined>(() => {
    for (const q of searchParams.entries()) {
      const match = q[0].match(/sortAttributes\[0\]\[(.+)\]/);

      if (match?.[1]) return { attribute: match[1], value: q[1] as 'asc' | 'desc' };
    }
    return { attribute: '', value: 'desc' }; // Relevance
  }, [searchParams]);

  const limitStep = useMemo(() => 40, []);

  const activeLimit = useMemo<number>(() => {
    return limit ? +limit : limitStep;
  }, [limit, limitStep]);

  const applyRefinements = useCallback(
    (facetsConfiguration: Record<string, FacetConfiguration>, sort?: Sort, limit?: number) => {
      const params = new URLSearchParams();

      if (uiState?.searchQuery) params.set('query', uiState.searchQuery);

      const selectedTags: string[] = [];

      Object.values(facetsConfiguration).forEach((configuration) => {
        if (!configuration.selected) return;

        if (configuration.type === 'term' || configuration.type === 'color') {
          if (configuration.key.toLowerCase().includes('tags')) {
            (configuration as TermFacet).terms
              .filter((term) => term.selected)
              .forEach((term) => selectedTags.push(term.key));
          }

          if (configuration.key === 'categories.categoryId') {
            (configuration as TermFacet).terms
              .filter((term) => term.selected)
              .forEach((term) => params.append('categories[]', term.key));
          } else {
            (configuration as TermFacet).terms
              .filter((term) => term.selected)
              .forEach((term, index) => params.set(`facets[${configuration.key}][terms][${index}]`, term.key));
          }
        } else if (configuration.type === 'boolean') {
          configuration.terms
            .filter((term) => term.selected)
            .forEach((term) => params.set(`facets[${configuration.key}][boolean]`, term.key));
        }
      });

      if (selectedTags.length > 0) {
        const matchingCategoryIds = getMatchingCategoryIdsByTags(categories, selectedTags);
        matchingCategoryIds.forEach((id) => params.append('categoryIds[]', id));
      }

      const queryString = globalThis.location.search;
      const urlParams = new URLSearchParams(queryString);
      if (urlParams?.has('channelKeys')) {
        const channelKey = urlParams.get('channelKeys');
        params.set(`channelKeys`, channelKey as string);
      }

      if (sort?.attribute) params.set(`sortAttributes[0][${sort.attribute}]`, sort.value);

      if (limit) params.set('limit', limit.toString());
      const currentPath = `${pathWithoutQuery}?${params.toString()}`;
      router.replace(currentPath, { scroll: false });

      postMessageNav(currentPath);
    },
    [uiState?.searchQuery, pathWithoutQuery, categories],
  );

  const activeRefinements = useMemo(() => {
    const refinements = [] as ActiveRefinement[];

    const addRefinement = (configuration: FacetConfiguration, label: string, refineCb: () => void) => {
      refinements.push({
        attribute: configuration.key,
        label,
        refine() {
          refineCb();

          applyRefinements(mergedFacetsConfiguration, activeSort, activeLimit);

          globalThis.dispatchEvent(
            new CustomEvent<RefinementRemovedEvent>(refinementRemovedEventName, {
              detail: { attribute: configuration.key },
            }),
          );
        },
      });
    };

    Object.values(mergedFacetsConfiguration).forEach((configuration) => {
      if (configuration.type === 'term' || configuration.type === 'color' || configuration.type === 'boolean') {
        (configuration as TermFacet).terms
          .filter((t: Term) => t.selected)
          .forEach((term: Term) =>
            addRefinement(configuration, term.label, createTermRefinementReset(configuration, term)),
          );
      }
    });

    return refinements;
  }, [currency, mergedFacetsConfiguration, locale, applyRefinements, activeSort, activeLimit]);

  const replaceSort = useCallback(
    (newSort: Sort) => {
      applyRefinements(mergedFacetsConfiguration, newSort, activeLimit);
    },
    [mergedFacetsConfiguration, applyRefinements, activeLimit],
  );

  const refine = useCallback(
    (attribute: string, key: string) => {
      const newFacetsConfiguration = cloneDeep(mergedFacetsConfiguration);

      if (!newFacetsConfiguration[attribute]) {
        newFacetsConfiguration[attribute] = {
          key: attribute,
          identifier: attribute,
          label: attribute,
          type: 'term',
          selected: false,
          terms: [],
        } as TermFacet;
      }

      const facet = newFacetsConfiguration[attribute] as TermFacet | BooleanFacet;

      let term = facet.terms.find((t) => t.key === key);

      if (!term) {
        term = {
          key: key,
          identifier: key,
          label: key,
          selected: false,
          count: 0,
        };
        facet.terms.push(term);
      }

      term.selected = !term.selected;

      if (facet.type === 'boolean')
        facet.terms.filter((t: Term) => t.key !== term?.key).forEach((t: Term) => (t.selected = false));

      facet.selected = facet.terms.some((t: Term) => t.selected);

      applyRefinements(newFacetsConfiguration, activeSort);
    },
    [mergedFacetsConfiguration, applyRefinements, activeSort],
  );

  const loadMore = useCallback(() => {
    applyRefinements(mergedFacetsConfiguration, activeSort, activeLimit + limitStep);
  }, [mergedFacetsConfiguration, activeSort, applyRefinements, activeLimit, limitStep]);

  const removeAllRefinements = useCallback(() => {
    const newFacetsConfiguration = cloneDeep(mergedFacetsConfiguration);

    Object.values(newFacetsConfiguration).forEach((configuration) => {
      if (configuration.type === 'term' || configuration.type === 'color' || configuration.type === 'boolean') {
        (configuration as TermFacet).terms.forEach((term) => (term.selected = false));
      }

      configuration.selected = false;
    });

    applyRefinements(newFacetsConfiguration, activeSort);

    globalThis.dispatchEvent(new CustomEvent(refinementsClearedEventName));
  }, [mergedFacetsConfiguration, applyRefinements, activeSort]);

  return (
    <ProductListContext.Provider
      value={{
        ...uiState,
        categories,
        shippingMethods,
        facetsConfiguration: mergedFacetsConfiguration,
        activeSort,
        activeLimit,
        activeRefinements,
        refine,
        replaceSort,
        removeAllRefinements,
        loadMore,
        wishlist,
        addToWishlist,
        removeFromWishlist,
        onAddToCart,
        isContainsP2RItems,
      }}
    >
      {children}
    </ProductListContext.Provider>
  );
};

export default ProductListProvider;

export const useProductList = () => useContext(ProductListContext);
